// ─────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────

import { exportedOnText } from './stamp';
import { alertService } from '../services/alertService';
export interface CsvColumn<T = Record<string, unknown>> {
  key: keyof T;
  label: string;
  format?: (value: unknown) => string | number;
}

export interface CsvExportOptions<T = Record<string, unknown>> {
  data: T[];
  columns: CsvColumn<T>[];
  fileName?: string;
  delimiter?: ',' | ';' | '\t';
  /** Optional heading written above the table (with an export timestamp). */
  title?: string;
}

export interface GridCsvExportOptions {
  rowData: Record<string, unknown>[];
  columnDefs: Array<{ field: string; headerName?: string }>;
  fileName?: string;
  delimiter?: ',' | ';' | '\t';
}

// ─────────────────────────────────────────────
// Auto-detect: infer formatter from key name
// (mirrors excel.ts — same keywords, same logic)
// ─────────────────────────────────────────────

const DATE_KEYWORDS = [
  'created',
  'updated',
  'date',
  'dob',
  'birthday',
  'expiry',
  'expires',
  'modified',
  'timestamp',
];

const CURRENCY_KEYWORDS = [
  'amt',
  'amount',
  'price',
  'cost',
  'fee',
  'charge',
  'revenue',
  'salary',
];

function inferFormatter(
  key: string
): ((value: unknown) => string | number) | null {
  const lower = key.toLowerCase();
  if (DATE_KEYWORDS.some((kw) => lower.includes(kw))) return formatDate;
  if (CURRENCY_KEYWORDS.some((kw) => lower.includes(kw))) return formatCurrency;
  return null;
}

// ─────────────────────────────────────────────
// Core: build CSV string from data + columns
// ─────────────────────────────────────────────

function buildCsvString<T = Record<string, unknown>>(
  data: T[],
  columns: CsvColumn<T>[],
  delimiter: string
): string {
  // Escape a cell value — wrap in quotes if it contains delimiter, quotes, or newlines
  const escape = (val: unknown): string => {
    const str = String(val ?? '');
    if (str.includes(delimiter) || str.includes('"') || str.includes('\n')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  // Header row
  const header = columns.map((col) => escape(col.label)).join(delimiter);

  // Data rows
  const rows = data.map((row) =>
    columns
      .map((col) => {
        const raw = (row as Record<string, unknown>)[col.key as string];
        const formatter = col.format ?? inferFormatter(col.key as string);
        const value = formatter ? formatter(raw) : (raw ?? '');
        return escape(value);
      })
      .join(delimiter)
  );

  return [header, ...rows].join('\n');
}

// ─────────────────────────────────────────────
// Core: trigger browser download of CSV string
// ─────────────────────────────────────────────

function downloadCsv(csvString: string, fileName: string): void {
  // BOM prefix ensures Excel opens UTF-8 CSV correctly (handles ₹, etc.)
  const bom = '\uFEFF';
  const blob = new Blob([bom + csvString], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');

  a.href = url;
  a.download = `${fileName}.csv`;
  a.style.display = 'none';

  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ─────────────────────────────────────────────
// Export: static columns or dynamic grid
// ─────────────────────────────────────────────

/**
 * Export a flat data array with a defined column map to CSV.
 * Auto-detects date and currency formatting from key names.
 *
 * @example
 * exportToCsv({
 *   data: users,
 *   columns: [
 *     { key: 'name',      label: 'Full Name'   },
 *     { key: 'email',     label: 'Email'       },
 *     { key: 'createdAt', label: 'Created'     }, // auto date
 *     { key: 'totalAmt',  label: 'Total'       }, // auto currency
 *   ],
 *   fileName: 'users-report',
 * });
 */
export function exportToCsv<T = Record<string, unknown>>({
  data,
  columns,
  fileName = 'export-csv',
  delimiter = ',',
  title,
}: CsvExportOptions<T>): void {
  if (!data.length) {
    alertService.warn('No data to export.');
    return;
  }

  const body = buildCsvString(data, columns, delimiter);
  const csv = title ? `${title}\n${exportedOnText()}\n\n${body}` : body;
  downloadCsv(csv, fileName);
}

// ─────────────────────────────────────────────
// Export: raw dynamic grid (columnDefs + rowData)
// for AG Grid / TanStack or similar
// ─────────────────────────────────────────────

/**
 * Export directly from a grid's rowData + columnDefs to CSV.
 * Preserves column order and uses headerName as the CSV header.
 *
 * @example
 * exportGridToCsv({
 *   rowData:    gridRef.current.api.getRenderedNodes().map(n => n.data),
 *   columnDefs: gridRef.current.api.getColumnDefs(),
 *   fileName:   'grid-export',
 * });
 */
export function exportGridToCsv({
  rowData,
  columnDefs,
  fileName = 'grid-export',
  delimiter = ',',
}: GridCsvExportOptions): void {
  if (!rowData.length) {
    console.warn('[exportGridToCsv] No rows to export.');
    return;
  }

  const columns: CsvColumn[] = columnDefs.map((col) => ({
    key: col.field,
    label: col.headerName ?? col.field,
  }));

  exportToCsv({ data: rowData, columns, fileName, delimiter });
}

// ─────────────────────────────────────────────
// Utility: format helpers for cell values
// (same as excel.ts — shared formatters)
// ─────────────────────────────────────────────

/** Format a JS Date or ISO string as DD/MM/YYYY */
export function formatDate(value: unknown): string {
  if (!value) return '';
  const d = new Date(value as string);
  if (isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('en-GB'); // DD/MM/YYYY
}

/** Format a number as currency string, e.g. "₹1,234.56" */
export function formatCurrency(value: unknown, symbol = '₹'): string {
  const num = Number(value);
  if (isNaN(num)) return '';
  return `${symbol}${num.toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** Format a decimal as percentage string, e.g. 0.25 → "25.00%" */
export function formatPercent(value: unknown, decimals = 2): string {
  const num = Number(value);
  if (isNaN(num)) return '';
  return `${(num * 100).toFixed(decimals)}%`;
}
